import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { ROUTES } from "../../../utils/constants";
import { LayoutPage } from "../../layoutPage";
import { ThemeProvider } from "@emotion/react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React, { useEffect, useMemo, useState } from "react";
import { IUser } from "../../../api/homesites/homesites.types";
import { useSelector } from "react-redux";
import { getAuth } from "../../../redux/auth/authSlice";
import { useSnackbarContext } from "../../../components/globalSnackNotify/globalSnackNotifyContext";
import { CustomToolbar, theme } from "../../../components/gridToolbarComponents/gridToolbarComponents";
import { TableColumnMap, useHomesite } from "../../../components/core/homesites/homesiteColumns";
import { useConciergeData } from "../../../reactQuery/concierge/conciergeQuery";
import { getDataGridRowStyles } from "../../../components/datagrid/datagridStyles"
import { HomeSitesDataIndex } from "../../../reactQuery/homeSitesDataIndex/types";
import { useHomeSiteDataIndexQuery } from "../../../reactQuery/homeSitesDataIndex/homeSiteDataIndexQuery";
import { useUpdateHomeSite } from "../../../reactQuery/homeSites/homsites/homeSiteQuery";
import { isConciergeUser } from "../../../utils/permissions";
import { UserRole } from "../../../utils/permissions";
import { BulkAssignConcierge } from "./BulkAssignConcierge";

interface ConciergeSelectCellProps {
  homesite: HomeSitesDataIndex;
  concierges: IUser[];
}

export const ConciergeSelectCell: React.FC<ConciergeSelectCellProps> = ({ homesite, concierges }) => {
  const [selectedConcierge, setSelectedConcierge] = useState<IUser>({
    name: homesite?.conciergeName,
    email: homesite?.conciergeEmail,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<IUser>(concierges[0]);
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const { showSnackbar } = useSnackbarContext();

  const updateHomeSite = useUpdateHomeSite(homesite.divisionCode);

  const successAssignConcierge = "Concierge Assigned Successfully!";
  const failedToAssignConcierge = "Failed to Assign Concierge";
  const successUnassignConcierge = "Concierge Removed Successfully!";
  const failedToUnassignConcierge = "Failed to Unassign Concierge";

  useEffect(() => {
    setSelectedConcierge(
      homesite.conciergeName
        ? {
          name: homesite.conciergeName,
          email: homesite.conciergeEmail,
        }
        : concierges[0]
    );
  }, [homesite, concierges]);

  const handleChange = (event: { target: { value: string } }) => {
    const value = event.target.value;
    if (value === "") {
      setUnassignDialogOpen(true);
    } else {
      const foundConcierge = concierges.find((c) => c.name === value);
      if (foundConcierge) {
        setPendingSelection(foundConcierge);
        setDialogOpen(true);
      }
    }
  };

  const handleConfirm = async () => {
    try {
      await updateHomeSite.mutateAsync({
        divisionCode: homesite.divisionCode,
        homesiteId: homesite.homeSiteId,
        body: {
          patches: [
            {
              op: "add",
              property: "/conciergeName",
              from: selectedConcierge.name,
              to: pendingSelection.name,
            },
            {
              op: "add",
              property: "/conciergeEmail",
              from: selectedConcierge.email,
              to: pendingSelection.email,
            },
          ],
        },
      });

      showSnackbar(successAssignConcierge, "success");
    } catch (error) {
      console.error("Error during PATCH request:", error);
      showSnackbar(failedToAssignConcierge, "error");
    }
    setDialogOpen(false);
  };

  const handleUnassign = async () => {
    try {
      await updateHomeSite.mutateAsync({
        divisionCode: homesite.divisionCode,
        homesiteId: homesite.homeSiteId,
        body: {
          patches: [
            {
              op: "add",
              property: "/conciergeName",
              from: selectedConcierge.name,
              to: null,
            },
            {
              op: "add",
              property: "/conciergeEmail",
              from: selectedConcierge.email,
              to: null,
            },
          ],
        },
      });
      showSnackbar(successUnassignConcierge, "success");
    } catch (error) {
      console.error(error);
      showSnackbar(failedToUnassignConcierge, "error");
    }
    setUnassignDialogOpen(false);
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setUnassignDialogOpen(false);
  };

  return (
    <>
      {homesite.conciergeSource ? (
        <span
          style={{
            color: "#9FB1BD",
            fontSize: "14px",
            fontFamily: "karla, sans-serif",
            marginLeft: "3px",
          }}
        >
          * {selectedConcierge?.name}
        </span>
      ) : (
        <Select
          value={selectedConcierge?.name ?? ""}
          onChange={handleChange}
          displayEmpty
          disabled={updateHomeSite.isPending}
          renderValue={(selected: string) => {
            if (!selected) {
              return <span style={{ color: "#5B7282", fontWeight: "lighter" }}>Unassigned</span>;
            }
            return (
              <Tooltip title={selected}>
                <span>{selected}</span>
              </Tooltip>
            );
          }}
          style={{ width: "100%", height: 30, scrollbarWidth: "none" }}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 200,
                overflowY: "auto",
                scrollbarWidth: "none",
              },
            },
          }}
        >
          {concierges.map((c) => {
            if (c.name === "") {
              if (selectedConcierge && selectedConcierge?.name) {
                return (
                  <MenuItem value="" key="unassign">
                    <span style={{ color: "red" }}>Unassign</span>
                  </MenuItem>
                );
              }
              return null;
            }
            return (
              <MenuItem key={c.email} value={c.name} disabled={selectedConcierge?.name === c?.name}>
                {c.name}
              </MenuItem>
            );
          })}
        </Select>
      )}
      <Dialog open={dialogOpen} onClose={handleCancel}>
        <DialogTitle>Assign Concierge</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to assign Homesite Number{" "}
            <span style={{ fontWeight: "bold", color: "black" }}>{homesite.homeSiteId}</span> to{" "}
            <span style={{ fontWeight: "bold", color: "black" }}>{pendingSelection.name}</span>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancel}
            color="primary"
            variant="outlined"
            style={{ textTransform: "none", border: "2px solid #005DAA" }}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="primary" variant="contained" style={{ textTransform: "none" }}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unassign Dialog */}
      <Dialog open={unassignDialogOpen} onClose={handleCancel}>
        <DialogTitle>Unassign Concierge</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to unassign{" "}
            <span style={{ fontWeight: "bold", color: "black" }}>{homesite.conciergeName}</span> from{" "}
            <span style={{ fontWeight: "bold", color: "black" }}>{homesite.homeSiteId}</span>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancel}
            color="primary"
            variant="outlined"
            style={{ textTransform: "none", border: "2px solid #005DAA" }}
          >
            Cancel
          </Button>
          <Button onClick={handleUnassign} color="primary" variant="contained" style={{ textTransform: "none" }}>
            Unassign
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const ConciergeAssignment = () => {
  const { userInfo } = useSelector(getAuth);
  const [community, setCommunity] = useState<string>("");
  const { filteredData: initialConciergeValues } = useConciergeData(userInfo.userDivCode);
  const { data: homesitesData, communities = [] } = useHomeSiteDataIndexQuery(userInfo.userDivCode);
  const noConcierge = useMemo(() => ({ name: "", email: "" }), []);
  const { getSortedColumns } = useHomesite();

  // Check if user is a concierge
  const isConcierge = useMemo(() => {
    const userRoles = (userInfo?.userRoles || []) as UserRole[];
    return isConciergeUser(userRoles);
  }, [userInfo?.userRoles]);

  /** Filter out the closed Homesites and find homesites which either have a trench Date or Sale Date */
  const homesites = useMemo(() => {
    return (
      homesitesData?.filter((hs) => ((hs.trenchDate != null || hs.saleDate != null) && hs.closeOfEscrowActual == null)) ||
      []
    );
  }, [homesitesData]);

  const concierges = useMemo(() => {
    const sorted = initialConciergeValues.sort((a, b) => a?.name.localeCompare(b.name));
    return [noConcierge, ...sorted];
  }, [initialConciergeValues, noConcierge]);

  // Get current user as concierge option
  const currentUserAsConcierge = useMemo(() => {
    if (!userInfo?.email || !userInfo?.name) return null;
    return {
      name: userInfo.name,
      email: userInfo.email,
    } as IUser;
  }, [userInfo?.email, userInfo?.name]);

  // Get concierges list including current user if they're a concierge
  const conciergesWithSelf = useMemo(() => {
    if (!isConcierge || !currentUserAsConcierge) return concierges;

    // Check if current user is already in the list
    const userExists = concierges.some(c => c.email === currentUserAsConcierge.email);
    if (userExists) return concierges;

    // Add current user to the list
    const sorted = [...concierges.filter(c => c.name !== ""), currentUserAsConcierge].sort((a, b) =>
      a?.name.localeCompare(b.name)
    );
    return [noConcierge, ...sorted];
  }, [concierges, currentUserAsConcierge, isConcierge, noConcierge]);


  const baseColumns = useMemo(() => {
    return getSortedColumns(
      false,
      ["homeSiteId", "communityName", "conciergeName"],
      TableColumnMap.ConciergeAssignmentFields
    );
  }, [getSortedColumns]);

  const finalColumns: GridColDef[] = useMemo(() => {
    const columns = baseColumns.map((col) => {
      if (col.field === "homeSiteId") {
        return {
          ...col,
          renderCell: (params: GridRenderCellParams<HomeSitesDataIndex>) => <div>{params.row.homeSiteId}</div>,
        };
      }

      if (col.field === "conciergeName") {
        return {
          ...col,
          renderCell: (params: GridRenderCellParams<HomeSitesDataIndex>) => (
            <ConciergeSelectCell homesite={params.row} concierges={concierges} />
          ),
        };
      }
      return col;
    });

    if (community) {
      return columns.filter((c) => c.field !== "communityName");
    }
    return columns;
  }, [baseColumns, community, concierges]);

  const getTitle = () => {
    if (community) {
      return (
        <h1 style={{ fontFamily: "Reckless", fontWeight: "normal" }}>
          <ArrowBackIcon style={{ marginRight: "16px" }} onClick={() => setCommunity("")} cursor="pointer" />
          {community}
        </h1>
      );
    }
    return (
      <LayoutPage title={ROUTES.closings.conciergeAssignment.label}>
        <p></p>
      </LayoutPage>
    );
  };

  const handleRowClick = (params: { row: { communityName: string; homeSiteId: string } }) => {
    setCommunity(params.row.communityName);
  };

  return (
    <ThemeProvider theme={theme}>
      <div>
        {getTitle()}
        <div
          style={{
            height: "100%",
            width: "100%",
            backgroundColor: "#fff",
            padding: "16px",
            borderRadius: "8px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <div>
              <BulkAssignConcierge
                userInfo={userInfo}
                homesites={homesites}
                communities={communities}
                conciergesWithSelf={conciergesWithSelf}
                isConcierge={isConcierge}
                currentCommunity={community}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <p>* Concierge Assigned in Salesforce</p>
            </div>
          </div>
          <Box
            sx={{
              "& .super-app-theme--header": {
                backgroundColor: "#DCE3E8",
              },
            }}
          >
            <div style={{ height: "76vh", width: "100%" }}>
              <DataGridPremium
                columns={finalColumns}
                rows={community ? homesites?.filter((hs) => hs.communityName === community) : homesites}
                rowHeight={35} /* By default row height is 52px */
                pagination
                onRowClick={!community ? handleRowClick : undefined}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 25, page: 0 },
                  },
                  sorting: {
                    sortModel: [{ field: "homeSiteId", sort: "asc" }],
                  },
                }}
                disableColumnSelector
                disableAggregation
                disableRowGrouping
                disableColumnPinning
                slots={{ toolbar: () => CustomToolbar(false, true, false) }}
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#DCE3E8 !important",
                    color: "black",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: "bold",
                  },
                  "& .MuiDataGrid-columnSeparator": {
                    visibility: "visible",
                    color: "divider",
                  },
                  "& .MuiDataGrid-cell": {
                    borderRight: "1px solid rgba(224, 224, 224, 1)",
                  },
                  ...getDataGridRowStyles(theme, false),
                }}
                getRowClassName={(params) => {
                  return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
                }}
              />
            </div>
          </Box>
        </div>
      </div>
    </ThemeProvider>
  );
};